/**
 * useSEO — per-host `<head>` injector.
 *
 * Fetches `/api/seo/meta` for the current apex host and drops:
 *   • `<title>`
 *   • `<meta name="description">`
 *   • `<link rel="canonical">`
 *   • `<meta property="og:title|og:description|og:url|og:type">`
 *   • `<script type="application/ld+json">` for Organization + WebSite + FAQPage
 *
 * into `document.head` at mount, and cleans them up on unmount. Runs
 * once at the top of the React tree (see `App.js`), so every page of
 * the SPA carries the correct SEO/AEO head tags — regardless of route
 * and regardless of whether a bot renders JavaScript (Googlebot,
 * PerplexityBot, ClaudeBot, GPTBot all render JS by default).
 *
 * The endpoint is host-aware (dispatch on the incoming `Host` header
 * on the backend), so the same `/api/seo/meta` call returns different
 * payloads for hustleplaza.au vs. hustleshop.au vs. tackle.au etc.
 *
 * Failure mode: silent no-op. SEO injection must never break a page.
 */
import { useEffect } from "react";

const OWNED = "hustlesuite-seo";

function upsertMeta(attr, value, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  const created = !el;
  if (created) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    el.setAttribute("data-owner", OWNED);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return created ? el : null;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (created) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-owner", OWNED);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return created ? el : null;
}

export default function useSEO() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // iter375 R5 (2026-02) — Raro-branded standalone site owns its
    // own <head> tags via <RaroSEO/> which reads business tokens.  The
    // global useSEO() must NOT overwrite them (was leaking a stale
    // demo title from /api/seo/meta over the Raro landing).
    const host = (window.location.hostname || "").toLowerCase();
    const path = window.location.pathname || "";
    const raroHosts = /^(www\.|app\.|demo\.)?(raro|hustleraro)\.(au|com\.au)$/;
    if (raroHosts.test(host) || path === "/raro" || path.startsWith("/raro/")) {
      return undefined;
    }

    // Some builds are served under a preview host (…preview.emergentagent.com)
    // whose Host header isn't in the SEO catalog. Bail early so we don't
    // add empty tags that would poison the real production /head.
    const currentHost = window.location.host || "";
    const looksLikePreview = /preview\.emergentagent\.com$/i.test(currentHost)
        || /localhost/i.test(currentHost)
        || /^\d+\.\d+\.\d+\.\d+/.test(currentHost);

    const API = process.env.REACT_APP_BACKEND_URL || "";
    const created = [];
    let alive = true;

    (async () => {
      try {
        const r = await fetch(`${API}/api/seo/meta`, {
          headers: { "Accept": "application/json" },
          credentials: "omit",
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!alive) return;
        if (!data.known && looksLikePreview) return;   // do not pollute preview

        // Basic meta tags
        if (data.title) {
          document.title = data.title;
        }
        [
          upsertMeta("name", "description", data.description || ""),
          upsertMeta("property", "og:title",       data.title || ""),
          upsertMeta("property", "og:description", data.description || ""),
          upsertMeta("property", "og:url",         data.canonical || ""),
          upsertMeta("property", "og:type",        "website"),
          upsertMeta("name", "robots",             "index,follow,max-image-preview:large"),
          upsertLink("canonical", data.canonical),
        ].forEach((el) => el && created.push(el));

        // JSON-LD blocks
        (data.schemas || []).forEach((schema) => {
          try {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-owner", OWNED);
            s.text = JSON.stringify(schema);
            document.head.appendChild(s);
            created.push(s);
          } catch (_) { /* schema too big / circular — skip */ }
        });
      } catch (_) {
        /* offline / CORS / preview — silent, must never break the page */
      }
    })();

    return () => {
      alive = false;
      // Only remove the nodes WE created — never touch tags authored
      // in index.html or by other hooks.
      created.forEach((el) => { try { el.remove(); } catch (_) {} });
    };
  }, []);
}
